import { X } from "lucide-react";
import { Preferences, PreviewFont, PreviewWidth, ThemeMode } from "../types";

interface SettingsPanelProps {
  open: boolean;
  preferences: Preferences;
  onClose: () => void;
  onChange: (patch: Partial<Preferences>) => void;
  onReset: () => void;
}

export const SettingsPanel = ({ open, preferences, onClose, onChange, onReset }: SettingsPanelProps) => {
  if (!open) return null;

  return (
    <div className="drawer-backdrop" role="presentation" onClick={onClose}>
      <section
        className="settings-drawer"
        aria-label="Settings"
        aria-modal="true"
        role="dialog"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="drawer-header">
          <h2>Settings</h2>
          <button className="icon-button" type="button" onClick={onClose} aria-label="Close settings">
            <X size={18} />
          </button>
        </div>

        <h3 className="drawer-section-title">Appearance</h3>
        <label>
          <span>Theme</span>
          <select value={preferences.theme} onChange={(event) => onChange({ theme: event.target.value as ThemeMode })}>
            <option value="system">System</option>
            <option value="light">Light</option>
            <option value="dark">Dark</option>
          </select>
        </label>

        <h3 className="drawer-section-title">Editor</h3>
        <label>
          <span>
            Font size <em className="setting-value">{preferences.editorFontSize}px</em>
          </span>
          <input
            type="range"
            min="13"
            max="20"
            value={preferences.editorFontSize}
            onChange={(event) => onChange({ editorFontSize: Number(event.target.value) })}
          />
        </label>
        <label>
          <span>Indent width</span>
          <select value={preferences.tabSize} onChange={(event) => onChange({ tabSize: Number(event.target.value) })}>
            <option value="2">2 spaces</option>
            <option value="4">4 spaces</option>
          </select>
        </label>
        <label className="check-row">
          <input type="checkbox" checked={preferences.wordWrap} onChange={(event) => onChange({ wordWrap: event.target.checked })} />
          <span>Word wrap</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={preferences.showLineNumbers}
            onChange={(event) => onChange({ showLineNumbers: event.target.checked })}
          />
          <span>Line numbers</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={preferences.spellcheck}
            onChange={(event) => onChange({ spellcheck: event.target.checked })}
          />
          <span>Spellcheck</span>
        </label>

        <h3 className="drawer-section-title">Preview</h3>
        <label>
          <span>
            Font size <em className="setting-value">{preferences.previewFontSize}px</em>
          </span>
          <input
            type="range"
            min="13"
            max="20"
            step="0.5"
            value={preferences.previewFontSize}
            onChange={(event) => onChange({ previewFontSize: Number(event.target.value) })}
          />
        </label>
        <label>
          <span>Content width</span>
          <select
            value={preferences.previewWidth}
            onChange={(event) => onChange({ previewWidth: event.target.value as PreviewWidth })}
          >
            <option value="compact">Compact</option>
            <option value="comfortable">Comfortable</option>
            <option value="wide">Wide</option>
          </select>
        </label>
        <label>
          <span>Typeface</span>
          <select
            value={preferences.previewFont}
            onChange={(event) => onChange({ previewFont: event.target.value as PreviewFont })}
          >
            <option value="sans">Sans-serif</option>
            <option value="serif">Serif</option>
          </select>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={preferences.syncScroll}
            onChange={(event) => onChange({ syncScroll: event.target.checked })}
          />
          <span>Sync preview scrolling</span>
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={preferences.enableMath}
            onChange={(event) => onChange({ enableMath: event.target.checked })}
          />
          <span>
            Math (LaTeX) <em className="setting-value">$x^2$ · $$…$$</em>
          </span>
        </label>

        <h3 className="drawer-section-title">Data</h3>
        <p className="drawer-note">
          Documents and settings are stored in this browser. Files saved to disk are not affected.
        </p>
        <button type="button" className="danger-button" onClick={onReset}>
          Reset app data…
        </button>
      </section>
    </div>
  );
};
